/**
 * What keeps the lead or the work from moving, in the order to fix it (wave B2, decision 0057), derived from the lead's
 * presence, her last turn, the workspace key and the runners. Moved here from the web in wave O1 (decision 0060)
 * unchanged in behaviour, so the cloud's `read_project.setup` and the web's banner say the same thing. Pure.
 */
import type { AgentKind, KeyStatus, Mind, SetupProblemTag } from "@minga/protocol"
import {
  type MayRunRunner,
  mayRun,
  mayUseProfile,
  profileAccessOf,
  sharedIn,
  subscriptionKey,
} from "./multiplayer"
import { WAITS_FOR_ACCESS } from "./presence"
import { accountChoiceKey } from "./subscription-accounts"
import type { ProjectState } from "./types"

export type SetupProblem =
  /** A cloud lead on the workspace key and that provider has no key (whatever her state), or her last turn said so. */
  | { readonly _tag: "no_key" }
  /** A cloud lead: the workspace key was refused (its test, or her last turn's auth_error). */
  | { readonly _tag: "key_refused" }
  /** A runner lead whose last turn ended auth_error: the coding tool on the runner is signed out. */
  | { readonly _tag: "cli_signed_out"; readonly runnerId: string | null; readonly cli: string }
  /** The workspace has no runner, or none online, and Lea (`lead`) or a waiting run needs one. */
  | { readonly _tag: "no_runner"; readonly lead?: boolean }
  /**
   * Lea's runner is offline: her pinned one, or (polish 2026-10-23 §5) the paired runner she would think on when every
   * runner is offline. `owner`: who the viewer reads it belongs to ("you", a name), filled in by the page that knows;
   * absent → "Lea's runner".
   */
  | { readonly _tag: "runner_offline"; readonly runnerId: string; readonly owner?: string }
  | { readonly _tag: "owner_left" } // presence reason "its owner left…"
  /**
   * Wave O1: a runner lead's runner(s) are online but none has a ready profile for her coding tool (a Codex-only runner
   * for a Claude Code Lea), or her turns wait with `missing_agent` / `profile_not_ready`. `runnerId`: her pinned runner.
   */
  | {
      readonly _tag: "no_cli"
      readonly cli: string
      readonly runnerId: string | null
      /**
       * Decision 0166 §2: nothing the viewer may use runs the lead, but this person's own login would if they shared it
       * (the lead's keeper's first): a member reads "Lea runs on Mira's machine, which runs only Mira's work".
       */
      readonly lockedTo?: string
      /** Where that login is: a machine of theirs, or their subscription on the Platform Runner. */
      readonly lockedOn?: "machine" | "subscription"
      /** `lockedTo` as the viewer reads it ("Mira"), filled in by the page that knows the names. */
      readonly owner?: string
      /** Found from the viewer's reach (`SetupInput.reach`), not from a waiting turn or the runner directory. */
      readonly reached?: true
      /**
       * Decision 0166 §3.6: a subscription the viewer may use here would run it, but they left it out of their choice
       * for this project: "none of your chosen subscriptions here has Claude Code", fixed in Project settings › Members.
       */
      readonly notChosen?: true
      /**
       * Re-walk 2026-10-28: the lead runs on the workspace's Platform Runner, so what is missing is a subscription for
       * its CLI, not an online runner ("no Codex subscription you may use is connected").
       */
      readonly hosted?: true
    }
  /** Decision 0135: a cloud lead's last turn found the provider account behind the key out of credit. */
  | { readonly _tag: "no_credit" }

/** The workspace key a cloud lead thinks with: missing, refused, or usable. */
export type WorkspaceKeyState = "missing" | "refused" | "ok"

export interface SetupInput {
  readonly lead: {
    readonly name: string
    /** The lead's presence state (`needs_attention` is the only one read here). */
    readonly state: string
    readonly reason: string | null
    readonly mind: "runner" | "cloud"
    readonly runnerId: string | null
    /** A runner lead's coding tool ("Claude Code", "Codex", "Antigravity"); absent → "the coding tool". */
    readonly cli?: string | null
    /** Re-walk 2026-10-28: it runs on the workspace's Platform Runner (`runnerId` is that runner). */
    readonly hosted?: boolean
  } | null
  readonly lastTurnError: string | null // the lead's last ended turn's error.code
  /** The runner the lead's last ended turn ran on (where its coding tool is signed out). */
  readonly lastTurnRunnerId?: string | null
  /** Where the lead's last ended turn ran ("runner" or "cloud"); absent → her mind decides. */
  readonly lastTurnOn?: "runner" | "cloud" | null
  /**
   * A cloud lead on the workspace's default credential: that provider's key. null/absent = not loaded, or the lead
   * doesn't use the workspace key (a runner mind, her own credential): nothing is claimed from it.
   */
  readonly workspaceKey?: WorkspaceKeyState | null
  /** null = not loaded: no runner problem is claimed. `onlineIds` names the online ones (a pinned runner's state). */
  readonly runners: {
    readonly total: number
    readonly online: number
    readonly onlineIds?: ReadonlyArray<string>
    /**
     * Polish 2026-10-23 §5: the paired runners that are offline, the ones the lead would think on first (her owner's).
     * With none online, a runner lead with one of these reads `runner_offline`, not "no runner". Absent → unknown.
     */
    readonly offlineIds?: ReadonlyArray<string>
  } | null
  readonly waitingForRunner: number // live runs in state `waiting` with no_runner_online / runner_offline
  /**
   * A runner lead: whether an online runner she can use (her pinned one, else any of her owner's) has a ready profile
   * for her coding tool. null/absent = unknown (no claim from it).
   */
  readonly cliReady?: boolean | null
  /** The lead's live turns waiting with `missing_agent` or `profile_not_ready` (no runner offers her coding tool). */
  readonly leadWaitingForCli?: number
  /**
   * Decision 0166 §2: could a turn this viewer asks start now (`leadReach`), before any turn waits? Given, it decides a
   * runner lead's runner problems: the Platform Runner starts on demand, so "no runner online" alone is no problem, and
   * a runner the viewer may not use is one. null/absent = unknown: the rules above apply (the cloud's
   * `read_project.setup`).
   */
  readonly reach?: LeadReach | null
}

/** Decision 0166 §2: a runner as `leadReach` reads it. */
export interface ReachRunner extends MayRunRunner {
  /** Online now, or (the Platform Runner) started on demand. */
  readonly available: boolean
  /** The Platform Runner: its profiles are people's subscriptions. */
  readonly hosted?: boolean
  /**
   * Decision 0168: someone else's personal machine as the viewer sees it: `profiles` lists only the logins shared with
   * the viewer (or theirs), the owner's personal ones are left out. Its owner's login could still run the turn.
   */
  readonly hidden?: boolean
  readonly profiles: ReadonlyArray<{
    readonly id: string
    readonly agent: AgentKind
    readonly ready: boolean
  }>
}

/** Decision 0166 §2: whether a turn this viewer asks could start now, and whose login would run it if shared. */
export interface LeadReach {
  readonly canRun: boolean
  /** Nothing the viewer may use runs it; this person's personal login would (the keeper's first). */
  readonly lockedTo: string | null
  readonly lockedOn: "machine" | "subscription" | null
  /**
   * Decision 0166 §3.6: it can't run only because of the viewer's choice of subscriptions in this project: one they
   * left out would run it. Absent = no.
   */
  readonly notChosen?: boolean
}

/**
 * Decision 0166 §2: the lead's turn as this viewer would ask it, before any turn waits. It may run on a runner that takes
 * it (`mayRun`: its keeper's machines and the team's, the Platform Runner among them) with a ready profile of the lead's
 * CLI the asker may use (`mayUseProfile`: their own subscription or one shared with the workspace or this project, and
 * among the ones they chose for this project, decision 0166 §3.2 and §3.6). A pinned runner or profile narrows the
 * choice as placement does. When none can, `notChosen` says whether the viewer's own choice is all that keeps it, else
 * `lockedTo` names the person whose personal login would (the keeper's first), so the line says whom to ask. Pure:
 * placement's own predicates.
 */
export const leadReach = (input: {
  readonly keeperId: string
  readonly viewerId: string
  readonly agent: AgentKind
  readonly pinnedRunner?: string | null
  readonly pinnedProfile?: string | null
  readonly runners: ReadonlyArray<ReachRunner>
  /** Decision 0166 §3.2: the project the turn runs in (absent or null: only workspace-wide sharing counts). */
  readonly projectId?: string | null
  /** Decision 0166 §3.6: the viewer's choice of subscriptions in this project (absent or null: all they may use). */
  readonly chosen?: ReadonlyArray<string> | null
}): LeadReach => {
  const run = {
    kind: "turn",
    ownerId: input.keeperId as never,
    turnAsker: input.viewerId === input.keeperId ? null : (input.viewerId as never),
    projectId: input.projectId ?? null,
  }
  const chosenRun = { ...run, chosen: input.chosen ?? null }
  const pinned = input.pinnedRunner ?? null
  const fits = (p: ReachRunner["profiles"][number]) =>
    input.pinnedProfile ? p.id === input.pinnedProfile : p.agent === input.agent
  const runners = input.runners.filter((r) => (pinned === null || r.id === pinned) && mayRun(r, run))
  const runsWith = (r: ReachRunner, p: ReachRunner["profiles"][number], asked: typeof run) =>
    r.available && fits(p) && p.ready && mayUseProfile(r, p.id, asked)
  for (const r of runners)
    if (r.profiles.some((p) => runsWith(r, p, chosenRun)))
      return { canRun: true, lockedTo: null, lockedOn: null }
  // decision 0166 §3.6: one the viewer may use here would run it, but they left it out of their choice
  if (input.chosen && runners.some((r) => r.profiles.some((p) => runsWith(r, p, run))))
    return { canRun: false, lockedTo: null, lockedOn: null, notChosen: true }
  let locked: { readonly ownerId: string; readonly on: "machine" | "subscription" } | null = null
  for (const r of runners)
    for (const p of r.profiles) {
      if (!fits(p) || mayUseProfile(r, p.id, run)) continue
      const ownerId = profileAccessOf(r, p.id).ownerId as string
      if (ownerId === input.viewerId) continue
      if (locked === null || (ownerId === input.keeperId && locked.ownerId !== input.keeperId))
        locked = { ownerId, on: r.hosted ? "subscription" : "machine" }
    }
  // decision 0168: someone else's personal machine hides its owner's own logins; one of them would run it if shared
  // (the pinned machine, or the keeper's): "Lea runs on Mira's machine" before any message, not "no runner"
  if (locked === null)
    for (const r of runners) {
      if (r.hidden !== true || !r.available || r.ownerId === input.viewerId) continue
      if (locked === null || (r.ownerId === input.keeperId && locked.ownerId !== input.keeperId))
        locked = { ownerId: r.ownerId as string, on: "machine" }
    }
  return { canRun: false, lockedTo: locked?.ownerId ?? null, lockedOn: locked?.on ?? null }
}

/** The state of the workspace key a mind thinks with; null when it doesn't use the workspace key or keys are unknown. */
export const workspaceKeyOf = (
  mind: Mind | null | undefined,
  keys: { readonly anthropic: KeyStatus | null; readonly openai: KeyStatus | null } | null | undefined,
): WorkspaceKeyState | null => {
  if (!mind || !keys || mind._tag !== "platform") return null
  const status = keys[mind.provider]
  if (status === null) return "missing"
  return status === "rejected" || status === "no_access" ? "refused" : "ok"
}

const CLI_NAME: Readonly<Record<AgentKind, string>> = {
  claude: "Claude Code",
  codex: "Codex",
  antigravity: "Antigravity", // decision 0109 (binary `agy`)
  muse: "Muse Code", // decision 0110
}

/** A runner family's product name: "Claude Code", "Codex", "Antigravity" or "Muse Code". */
export const cliName = (agent: AgentKind): string => CLI_NAME[agent]

/** "Claude Code", "Codex", "Antigravity" or "Muse Code" for a runner mind; null otherwise. */
export const cliOfMind = (mind: Mind | null | undefined): string | null =>
  mind?._tag === "runner" ? cliName(mind.agent) : null

/** Decision 0166 §1.3: a Platform Runner subscription as the lead's default reads it. */
export interface PlatformSubscriptionFacts {
  /** The subscription's id, for the asker's choice (§3.6); absent = not checked against a choice. */
  readonly id?: string | undefined
  readonly agent: "claude" | "codex"
  /** Whose subscription it is (absent on old rows: the person who set the Platform Runner up). */
  readonly ownerUserId?: string | undefined
  /** `team` = shared with everyone in the workspace; `projects` = in `projectIds` only; absent = personal. */
  readonly scope?: string | undefined
  /** Decision 0166 §3.1: the projects a `projects` scope shares it in. */
  readonly projectIds?: ReadonlyArray<string> | undefined
  /** Decision 0167 §6: its account (absent on another person's subscription as a member sees it). */
  readonly quotaAccountKey?: string | undefined
}

/** Decision 0166 §3.2 and §3.6: where the lead works and what the person chose there. */
export interface LeadWhere {
  /** The lead's project (absent or null: a new project, where only workspace-wide sharing counts). */
  readonly projectId?: string | null | undefined
  /** The person's choice of subscriptions in that project (`subscriptionKey`s; absent or null: all they may use). */
  readonly chosen?: ReadonlyArray<string> | null | undefined
}

/**
 * Decision 0166 §1.3, with §3.2 and §3.6: the Platform Runner subscriptions a person's turns may use: their own and
 * others' shared with the workspace or with this project (`sharedIn`, as `mayUseProfile` reads it), narrowed to the
 * ones they chose for this project. Own ones first. Pure.
 */
export const usablePlatformSubscriptions = <S extends PlatformSubscriptionFacts>(
  platform: {
    readonly runnerId: string | null
    readonly ownerUserId?: string | null
    readonly subscriptions: ReadonlyArray<S>
  } | null,
  userId: string,
  where: LeadWhere = {},
): { readonly own: ReadonlyArray<S>; readonly shared: ReadonlyArray<S> } => {
  if (!platform?.runnerId) return { own: [], shared: [] }
  const runnerId = platform.runnerId
  const ownerOf = (s: S) => s.ownerUserId ?? platform.ownerUserId ?? null
  // decision 0167 §6: a chosen account covers the subscription too
  const chosen = (s: S) =>
    !where.chosen ||
    s.id === undefined ||
    where.chosen.includes(subscriptionKey(runnerId, s.id)) ||
    (s.quotaAccountKey !== undefined &&
      ownerOf(s) !== null &&
      where.chosen.includes(accountChoiceKey(ownerOf(s) as string, s.quotaAccountKey)))
  const scoped = (s: S) =>
    sharedIn(
      { scope: (s.scope ?? "personal") as never, projectIds: (s.projectIds ?? []) as never },
      where.projectId,
    )
  return {
    own: platform.subscriptions.filter((s) => ownerOf(s) === userId && chosen(s)),
    shared: platform.subscriptions.filter((s) => ownerOf(s) !== userId && scoped(s) && chosen(s)),
  }
}

/**
 * Decision 0166 §1.3: the new lead's default mind: the workspace's Platform Runner with the person's own subscription,
 * else one shared with them (with the workspace, or with the lead's project: §3.2), in the CLI that subscription is for
 * (the caller's coding tool first when both are there). Only subscriptions the person chose for the project count
 * (§3.6), so the default agrees with `mayUseProfile`. Pinned to the runner, not to a subscription: each turn uses its
 * asker's own subscription there or a shared one, so a member's message runs on theirs. Null without a Platform Runner
 * or a subscription the person may use. Pure.
 */
export const platformLeadMind = (
  platform: {
    readonly runnerId: string | null
    readonly ownerUserId?: string | null
    readonly subscriptions: ReadonlyArray<PlatformSubscriptionFacts>
  } | null,
  userId: string,
  preferAgent: "claude" | "codex" | null = null,
  where: LeadWhere = {},
): Mind | null => {
  if (!platform?.runnerId) return null
  const { own, shared } = usablePlatformSubscriptions(platform, userId, where)
  const pick = (subs: ReadonlyArray<PlatformSubscriptionFacts>) =>
    subs.find((s) => s.agent === preferAgent) ?? subs[0]
  const s = pick(own) ?? pick(shared)
  if (!s) return null
  return {
    _tag: "runner",
    agent: s.agent,
    requires: { runner: platform.runnerId as never },
    profileId: null,
    model: null,
    effort: null,
  }
}

/**
 * Lea "can't work" = no key, key refused, her coding tool signed out, owner left, or (a runner mind and no runner / her
 * runner offline). A cloud lead on the workspace key with no key for her provider can't work before anyone writes to
 * her: no_key shows whatever her state. A runner problem with a cloud Lea still shows while runs wait for a runner.
 * Nothing is claimed while the runners load.
 */
export const setupProblems = (i: SetupInput): ReadonlyArray<SetupProblem> => {
  const out: SetupProblem[] = []
  const lead = i.lead
  const runnerMind = lead?.mind === "runner"
  const reach = runnerMind ? (i.reach ?? null) : null
  if (i.runners !== null && reach !== null) {
    // decision 0166 §2: the viewer's own reach decides, before a turn waits; her pinned runner asleep is said as such
    const pinned = lead?.runnerId ?? null
    // the Platform Runner starts on demand: listed offline between tasks, never asleep
    const pinnedAsleep =
      pinned !== null &&
      lead?.hosted !== true &&
      i.runners.onlineIds !== undefined &&
      !i.runners.onlineIds.includes(pinned)
    const none = i.runners.total === 0 || i.runners.online === 0
    if (!reach.canRun && pinnedAsleep && reach.lockedTo === null && reach.notChosen !== true)
      out.push({ _tag: "runner_offline", runnerId: pinned })
    else if (!reach.canRun || (i.leadWaitingForCli ?? 0) > 0)
      out.push({
        _tag: "no_cli",
        cli: lead?.cli ?? "the coding tool",
        runnerId: pinned,
        ...(!reach.canRun ? { reached: true as const } : {}),
        ...(!reach.canRun && reach.notChosen === true ? { notChosen: true as const } : {}),
        ...(!reach.canRun && reach.lockedTo !== null ? { lockedTo: reach.lockedTo } : {}),
        ...(!reach.canRun && reach.lockedOn !== null ? { lockedOn: reach.lockedOn } : {}),
        ...(lead?.hosted === true ? { hosted: true as const } : {}),
      })
    else if (none && i.waitingForRunner > 0) out.push({ _tag: "no_runner", lead: false })
  } else if (i.runners !== null) {
    const none = i.runners.total === 0 || i.runners.online === 0
    const pinned = runnerMind ? (lead?.runnerId ?? null) : null
    const pinnedOffline =
      pinned !== null && !none && i.runners.onlineIds !== undefined && !i.runners.onlineIds.includes(pinned)
    // polish 2026-10-23 §5: paired but asleep is not "no runner": her pinned runner, else the first offline one
    const asleep =
      runnerMind && i.runners.total > 0 && i.runners.online === 0
        ? (pinned ?? i.runners.offlineIds?.[0] ?? null)
        : null
    if (asleep !== null) out.push({ _tag: "runner_offline", runnerId: asleep })
    else if (none && (runnerMind || i.waitingForRunner > 0)) out.push({ _tag: "no_runner", lead: runnerMind })
    else if (pinnedOffline && pinned !== null) out.push({ _tag: "runner_offline", runnerId: pinned })
    // a runner is up, but none offers her coding tool ready (a Codex-only runner for a Claude Code Lea)
    else if (runnerMind && !none && (i.cliReady === false || (i.leadWaitingForCli ?? 0) > 0))
      out.push({ _tag: "no_cli", cli: lead?.cli ?? "the coding tool", runnerId: pinned })
  }
  const key = lead && !runnerMind ? (i.workspaceKey ?? null) : null
  if (key === "missing") out.push({ _tag: "no_key" })
  else if (key === "refused") out.push({ _tag: "key_refused" })
  // re-walk 0166: a turn waiting on access is said by the reach (`no_cli`) or by the turn's own line, not as a sign-in
  if (lead?.state === "needs_attention" && lead.reason !== WAITS_FOR_ACCESS) {
    // The failed turn's own executor says where the sign-in failed; her mind when it is unknown.
    const failedOnRunner = i.lastTurnOn ? i.lastTurnOn === "runner" : runnerMind
    if (lead.reason?.startsWith("its owner left")) out.push({ _tag: "owner_left" })
    else if (i.lastTurnError === "credit_exhausted") out.push({ _tag: "no_credit" })
    else if (failedOnRunner)
      out.push({
        _tag: "cli_signed_out",
        runnerId: i.lastTurnRunnerId ?? lead.runnerId ?? null,
        cli: lead.cli ?? "the coding tool",
      })
    else if (key === null) out.push({ _tag: i.lastTurnError === "no_api_key" ? "no_key" : "key_refused" })
    // A known key that is usable now: the failed turn predates it (added or replaced since); the next turn uses it.
  }
  return out
}

/** Decision 0168: how a turn may end without having failed (the person's stop, a newer attempt, done). */
const NOT_FAILED: ReadonlySet<string> = new Set(["completed", "interrupted", "cancelled", "superseded"])

/**
 * Decision 0168: the agent's newest chat turn ended in a failure (sandbox didn't start, CLI error, signed out, …) and
 * no turn came after it: Home, the header, the chip and the strip say "last turn failed", never "idle" with an online
 * dot. A retry is a new turn, so this clears as soon as it is requested. Pure.
 */
export const leadTurnFailed = (
  s: Pick<ProjectState, "runs">,
  agentId: string,
): { readonly runId: string; readonly stop: string } | null => {
  let newest: ProjectState["runs"][number] | null = null
  for (const r of s.runs)
    if (r.kind === "turn" && r.agentId === agentId && (newest === null || r.requestedAt > newest.requestedAt))
      newest = r
  if (newest?.state._tag !== "ended" || NOT_FAILED.has(newest.state.stopReason)) return null
  return { runId: newest.id, stop: newest.state.stopReason }
}

/** Does this problem keep Lea herself from working? */
export const blocksLead = (p: SetupProblem): boolean => (p._tag === "no_runner" ? p.lead === true : true)

/** "Jannik's", "Jess'". */
const possessive = (name: string): string => (name.endsWith("s") ? `${name}'` : `${name}'s`)

const PART: {
  readonly [K in SetupProblem["_tag"]]: (name: string, p: Extract<SetupProblem, { _tag: K }>) => string
} = {
  no_key: () => "no model key",
  key_refused: () => "the model key was refused",
  cli_signed_out: (name, p) => `${p.cli} is signed out on ${name}'s runner`,
  no_runner: () => "no runner",
  runner_offline: (name, p) =>
    p.owner === "you"
      ? "your runner is offline"
      : `${p.owner ? possessive(p.owner) : possessive(name)} runner is offline`,
  owner_left: (name) => `${name}'s owner left the workspace`,
  no_cli: (_name, p) =>
    p.notChosen === true
      ? `none of your chosen subscriptions here has ${p.cli}`
      : p.lockedTo !== undefined
        ? `${p.cli} here is ${p.owner ? possessive(p.owner) : "someone else's"} and runs only their own work`
        : (p.runnerId === null || p.hosted === true) && p.reached === true
          ? `no ${p.cli} subscription you may use is connected`
          : `no online runner has ${p.cli} ready`,
  no_credit: () => "the model account is out of credit",
}
const partOf = (p: SetupProblem, name: string): string =>
  (PART[p._tag] as (name: string, p: SetupProblem) => string)(name, p)

const andList = (xs: ReadonlyArray<string>): string =>
  xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`

/** "Lea can't work right now: no model key and no runner." / "2 items are waiting for a runner: none is online." */
export const setupSentence = (
  problems: ReadonlyArray<SetupProblem>,
  leadName: string,
  waiting: number,
): string => {
  if (problems.length === 0) return ""
  const lea = problems.filter(blocksLead).map((p) => partOf(p, leadName))
  const runnerProblem = problems.some((p) => p._tag === "no_runner" || p._tag === "runner_offline")
  const items = waiting === 1 ? "1 item is" : `${waiting} items are`
  const waits =
    runnerProblem && waiting > 0
      ? problems.some((p) => p._tag === "no_runner")
        ? `${items} waiting for a runner: none is online.`
        : `${items} waiting for a runner.`
      : null
  // decision 0166 §2: a member whose turn only someone else's personal login could run reads whose it is
  const blocking = problems.filter(blocksLead)
  const only = blocking.length === 1 ? blocking[0] : undefined
  const first =
    only?._tag === "no_cli" && only.lockedTo !== undefined && only.owner !== undefined
      ? `${leadName} runs on ${possessive(only.owner)} ${only.lockedOn === "subscription" ? "subscription" : "machine"}, which runs only ${possessive(only.owner)} work.`
      : lea.length > 0
        ? `${leadName} can't work right now: ${andList(lea)}.`
        : null
  return [first, waits].filter((x) => x !== null).join(" ")
}

// The protocol's `ProjectSetup.problems` names exactly these tags (a compile-time check both ways).
type _SameTags = [SetupProblem["_tag"]] extends [SetupProblemTag]
  ? [SetupProblemTag] extends [SetupProblem["_tag"]]
    ? true
    : never
  : never
const _sameTags: _SameTags = true
